import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  AdminJobsTableCellOrder,
  AdminJobsTableHeaders,
} from "Modules/Core/Admin/AdminDashboard/adminDashboardConstants";
import { JobsFooter } from "Modules/Core/JobsTs/JobsListTs/JobsFooter";
import { IsSmScreen, useAppDispatch, useAppSelector } from "helpers/hooks";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import ShAdminTable from "shared/SharedComponents/ShAdminTable/ShAdminTable";
import SHTableSearchBar from "shared/SharedComponents/ShAdminTable/ShAdminTableSearchbar";
import {
  IJobInfoResponse,
  IPaginationParams,
  JobReviewsStates,
  RootState,
} from "shared/SharedModels";
import { DefaultPageSize } from "shared/constants";
import { getAdminJobs, deleteEmployerJob } from "store/slices/admin/jobs-reviews-slice";
import DeleteIcon from "@mui/icons-material/DeleteRounded";
import { ITableHeadersBase } from "shared/SharedModels";
import { Tooltip } from "@mui/material";
export const AdminJobs = () => {
  const [pageTitle, setPageTitle] = useState<string>("");
  const [tableTitle, setTableTitle] = useState<string>("");
  const [tableRows, setTableRows] = useState<
    Array<IJobInfoResponse & { showDelete?: boolean }>
  >([]);
  const [searchKey, setSearchKey] = useState<string>("");
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [paginationParams, setPaginationParams] = useState<IPaginationParams>({
    count: 0,
    requestedPageNumber: 1,
    requestedPageSize: DefaultPageSize,
    totalPages: 0,
  });
  const {
    pendingJobs,
    approvedJobs,
    rejectedJobs,
    removedJobs,
    isFetchingJobs,
  } = useAppSelector((state: RootState) => state.admin.jobReviews);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const activePathname = location.pathname.split("/").pop();
  const isSmScreen = IsSmScreen();
  const handleDelete = async (jobId: number) => {
    try {
      const resultAction = await dispatch(deleteEmployerJob({ jobId: jobId.toString() }));
      if (deleteEmployerJob.fulfilled.match(resultAction)) {
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.pending,
            pageNo: 1,
            pageSize: 20,
          })
        );
      } else {
        console.error("Delete failed:", resultAction);
      }
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };
  useEffect(() => {
    const concatSdfcLink = (jobs: Array<IJobInfoResponse>) =>
      jobs.map((job) => ({
        ...job,
        company_name: `${job.company_name} | Link`,
        showDelete:
          activePathname === "pending-jobs" && selectedJobId === job.id,
      }));
    switch (activePathname) {
      case "pending-jobs":
        setTableRows(concatSdfcLink(pendingJobs));
        break;
      case "approved-jobs":
        setTableRows(concatSdfcLink(approvedJobs));
        break;
      case "rejected-jobs":
        setTableRows(concatSdfcLink(rejectedJobs));
        break;
      case "removed-jobs":
        setTableRows(concatSdfcLink(removedJobs));
        break;
      case "pending-update-jobs":
        setTableRows(concatSdfcLink([]));
        break;
    }
  }, [
    pendingJobs,
    approvedJobs,
    rejectedJobs,
    removedJobs,
    activePathname,
    selectedJobId,
  ]);
  useEffect(() => {
    switch (activePathname) {
      case "pending-jobs":
        setPageTitle("Pending Queue");
        setTableTitle("New Jobs Pending Approval");
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.pending,
            pageNo: 1,
            pageSize: 20,
          })
        );
        break;
      case "approved-jobs":
        setPageTitle("Approval Queue");
        setTableTitle("New Approved Jobs");
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.approved,
            pageNo: 1,
            pageSize: 20,
          })
        );
        break;
      case "rejected-jobs":
        setPageTitle("Rejected Queue");
        setTableTitle("Rejected Jobs");
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.rejected,
            pageNo: 1,
            pageSize: 20,
          })
        );
        break;
      case "removed-jobs":
        setPageTitle("Removed Queue");
        setTableTitle("Removed Jobs");
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.removed,
            pageNo: 1,
            pageSize: 20,
          })
        );
        break;
      case "pending-update-jobs":
        setPageTitle("Pending Updates Queue");
        setTableTitle("New Jobs Pending Updates");
        dispatch(
          getAdminJobs({
            state: JobReviewsStates.updatesPending,
            pageNo: 1,
            pageSize: 20,
          })
        );
        break;
    }
  }, [activePathname, dispatch]);
  const searchJob = (searchKey: string) => {
    // implement search logic
  };
  const handleGetJobs = (pageNo: number, pageSize: number) => {
    dispatch(
      getAdminJobs({ state: JobReviewsStates.pending, pageNo, pageSize })
    );
    setPaginationParams((params) => ({
      ...params,
      requestedPageNumber: pageNo,
      requestedPageSize: pageSize,
    }));
  };
  const extendedHeaders: ITableHeadersBase<string, string>[] = [
    ...AdminJobsTableHeaders,
    ...(activePathname === "pending-jobs"
      ? [
          {
            columnName: "actions",
            label: "Actions",
            render: (row: IJobInfoResponse & { showDelete?: boolean }) => (
              <Box onClick={() => setSelectedJobId(row.id)} ml={1}>
                <Tooltip title="Delete job from pending list">
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row.id);
                    }}
                  >
                    <DeleteIcon color="error" />
                  </IconButton>
                </Tooltip>
              </Box>
            ),
          },
        ]
      : []),
  ];
  return (
    <Box p={4}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h6" fontWeight="700">
          {pageTitle}
        </Typography>
        <SHTableSearchBar
          searchKey={searchKey}
          setSearchKey={setSearchKey}
          onSearch={(searchKey) => searchJob(searchKey)}
        />
      </Stack>
      <ShAdminTable
        title={tableTitle}
        headers={extendedHeaders}
        rows={isFetchingJobs === "pending" ? [] : tableRows}
        cellOrder={AdminJobsTableCellOrder}
        redirectionUrlsJobId={["name"]}
        redirectionUrlsEmployerId={["company_name"]}
        generateUrlJobId={(value: string) => `job-reviews/${value}/edit`}
        generateUrlEmployerId={(value: string) => `employers/${value}/edit`}
        generateUrlById
        isLoading={isFetchingJobs === "pending"}
        onRowClick={(row: IJobInfoResponse) => {
          if (activePathname === "pending-jobs") {
            setSelectedJobId(row.id);
          }
        }}
      />
      <JobsFooter
        paginationParams={paginationParams}
        isSmScreen={isSmScreen}
        getJobs={(pageNo, pageSize) => {
          if (pageNo && pageSize) handleGetJobs(pageNo, pageSize);
        }}
      />
    </Box>
  );
};